<template>
  <div class="w-full">
    <div class="w-full bg-panels-gradient overflow-y-auto">
      <ul class="h-20 flex text-center">
        <li class="mx-3 xl:mx-6 flex flex-row">
          <a
            href="#"
            class="
              h-full
              border-b-4
              border-transparent
              hover:border-brand
              no-underline
              text-grey-darkest
              uppercase
              font-semibold
              text-xs
              md:text-base
              flex
              items-center
            "
            :class="{ 'border-brand': filters.selected_role == 'all' }"
            @click.prevent="
              filters.selected_role = 'all';
              filters.role_id = '';
              filterUsers();
            "
            >All</a
          >
        </li>
        <li
          v-for="role in roles"
          :key="role.id"
          class="mx-3 xl:mx-6 flex flex-row"
        >
          <a
            href="#"
            class="
              h-full
              border-b-4
              border-transparent
              hover:border-brand
              no-underline
              text-grey-darkest
              uppercase
              font-semibold
              text-xs
              md:text-base
              flex
              items-center
            "
            :class="{
              'border-brand': filters.selected_role == sanitizeTitle(role),
            }"
            @click.prevent="
              filters.selected_role = sanitizeTitle(role);
              filters.role_id = role.id;
              filterUsers();
            "
            >{{ sanitizeTitle(role) }}</a
          >
        </li>
      </ul>
    </div>

    <div class="flex p-4 flex-col md:flex-row relative">
      <!-- Directory column: invite, search, paging, list. One markup for every screen size. -->
      <div class="flex flex-col w-full md:w-64 md:flex-none md:border-r md:pr-4">
        <div v-is-authorized="{ roles: [role], permissions: ['can_add_members'] }">
          <router-link
            :to="{ name: 'invite-members', params: { vault_id: $route.params.vault_id } }"
            class="block text-center border border-brand bg-white text-brand text-sm font-medium py-2 px-4 rounded no-underline hover:bg-brand hover:text-white"
          >
            <i class="fa fa-user-plus mr-1"></i> Invite members
          </router-link>
        </div>

        <input
          type="text"
          class="border rounded py-2 px-3 mt-4 w-full text-sm"
          placeholder="Search members"
          v-model="filters.name"
          @input="filterUsers"
        />

        <div class="flex items-center justify-between mt-2 text-sm text-grey-darker" v-if="!searching && members.total > 0">
          <span>{{ countSentence }}</span>
          <span class="flex" v-if="members.last_page > 1">
            <button
              type="button"
              class="border border-grey bg-white rounded py-1 px-2 hover:border-brand hover:text-brand"
              :class="{ 'opacity-50 cursor-not-allowed': members.current_page == 1 }"
              :disabled="members.current_page == 1"
              @click.prevent="filterUsers(members.current_page - 1)"
              aria-label="Previous page"
            >
              <i class="fa fa-chevron-left"></i>
            </button>
            <button
              type="button"
              class="border border-grey bg-white rounded py-1 px-2 ml-1 hover:border-brand hover:text-brand"
              :class="{ 'opacity-50 cursor-not-allowed': members.current_page == members.last_page }"
              :disabled="members.current_page == members.last_page"
              @click.prevent="filterUsers(members.current_page + 1)"
              aria-label="Next page"
            >
              <i class="fa fa-chevron-right"></i>
            </button>
          </span>
        </div>

        <div class="p-4 mt-4 bg-grey-light text-grey-darker text-center rounded" v-if="searching">
          <i class="fa fa-spin fa-circle-notch"></i> Loading members
        </div>

        <div v-if="!searching && members.total > 0" class="mt-2 overflow-y-auto">
          <div
            v-for="member in members.data"
            :key="member.id"
            class="flex items-center px-2 py-2 rounded cursor-pointer hover:bg-grey-lighter border-b border-grey-light md:border-b-0"
            :class="{ 'bg-brand-lightest': selected_member.id == member.id }"
            @click="selected_member = member"
          >
            <div
              class="flex-none h-10 w-10 rounded-full flex items-center justify-center text-white text-lg font-medium"
              :class="selected_member.id == member.id ? 'bg-brand' : 'bg-grey-light'"
            >
              {{ oneCharName(member) }}
            </div>
            <div class="flex-1 min-w-0 ml-3">
              <p class="truncate" :class="selected_member.id == member.id ? 'font-medium text-black' : 'text-grey-darkest'">
                {{ member.first_name }} {{ member.last_name }}
              </p>
              <p class="text-xs text-grey-dark truncate">
                {{ sanitizeTitle(member.roles[0]) }}
                <span v-if="member.pivot.expires_at">
                  <i class="fa fa-stopwatch ml-1"></i> {{ formatDate(member.pivot.expires_at, "D MMM YYYY") }}
                </span>
              </p>
            </div>
          </div>
        </div>

        <div class="p-4 mt-4 bg-grey-light text-grey-darker text-center rounded" v-if="!searching && members.total == 0">
          <span v-if="filters.name || filters.role_id">No members match.</span>
          <span v-else>This vault has no members yet.</span>
        </div>
      </div>

      <!-- Desktop detail panel -->
      <div class="hidden md:block flex-1 md:pl-6">
        <p class="text-grey-darker mt-2" v-if="selected_member == ''">Select a member to see their details.</p>
        <member-card v-else :member="selected_member" />
      </div>

      <!-- Mobile: the details slide over the directory -->
      <transition name="slideLeft">
        <div class="md:hidden absolute inset-0 bg-white p-4" v-if="selected_member != ''">
          <button type="button" class="text-left py-2 font-medium text-brand" @click.prevent="selected_member = ''">
            <i class="fa fa-chevron-left mr-1"></i> Back to directory
          </button>
          <member-card :member="selected_member" class="mt-2" />
        </div>
      </transition>
    </div>
  </div>
</template>

<script>
import { mapGetters } from "vuex";

// Labelled values in the Vault Details style. Shown on the right on desktop
// and in the slide-over on mobile, from one piece of markup.
const MemberCard = {
  props: ["member"],
  inject: ["sanitizeTitle", "oneCharName", "formatDate"],
  template: `
    <div class="border rounded p-4 bg-brand-lightest">
      <div class="flex items-center">
        <div class="flex-none h-16 w-16 rounded-full bg-brand flex items-center justify-center text-white text-2xl font-medium">
          {{ oneCharName(member) }}
        </div>
        <div class="ml-4 min-w-0">
          <p class="text-xl font-medium text-black break-words">{{ member.first_name }} {{ member.last_name }}</p>
          <p class="text-sm text-grey-darker">{{ sanitizeTitle(member.roles[0]) }}</p>
        </div>
      </div>
      <div class="flex flex-wrap mt-4">
        <div class="w-full sm:w-1/2 py-2 sm:pr-4" v-for="item in details" :key="item.label">
          <p class="text-xxs uppercase tracking-wide text-grey-dark font-semibold">{{ item.label }}</p>
          <p class="break-words">{{ item.value || "-" }}</p>
        </div>
      </div>
    </div>
  `,
  computed: {
    // Not "fields": vee-validate adds a "fields" computed to every component and wins.
    details() {
      const pivot = this.member.pivot || {};
      const address = [this.member.address, this.member.city, this.member.state].filter(Boolean).join(" ");
      return [
        { label: "Email", value: this.member.email },
        { label: "Mobile", value: this.member.mobile_number },
        { label: "Company", value: this.member.company_name },
        { label: "Postal address", value: address },
        { label: "Added on", value: pivot.created_at ? this.formatDate(pivot.created_at, "D MMM YYYY") : "" },
        { label: "Expires on", value: pivot.expires_at ? this.formatDate(pivot.expires_at, "D MMM YYYY") : "" },
      ];
    },
  },
};

export default {
  components: { MemberCard },

  provide() {
    return {
      sanitizeTitle: this.sanitizeTitle,
      oneCharName: this.oneCharName,
      formatDate: this.formatDate,
    };
  },

  data() {
    return {
      members: {},
      user_files: {},

      searching: false,

      selected_member: "",

      filters: {
        selected_role: "all",
        role_id: "",
        name: "",
      },

      custom_header: {
        title: ["User A"],
      },
    };
  },

  mounted() {
    this.filterUsers();
    this.getRoles();
  },

  methods: {
    filterUsers: _.debounce(function (next) {
      this.selected_member = "";
      this.searching = true;
      axios
        .get(`/api/vaults/${this.$route.params.vault_id}/users`, {
          params: {
            name: this.filters.name,
            role_id: this.filters.role_id,
            page: next,
          },
        })
        .then(({ data }) => {
          this.searching = false;
          this.members = data;
        });
    }, 500),

    getRoles() {
      axios
        .get(`/api/vaults/${this.$route.params.vault_id}/roles`)
        .then(({ data }) => {
          this.$store.commit("setVaultRoles", data);
        });
    },

    sanitizeTitle(role) {
      if (window.vault_type == "strata") {
        return role.title.replace(/.+?(\- )/g, "");
      }

      if (window.vault_type == "business") {
        let vault_id = this.current_vault.id;
        let role_name = role.name;
        let replace_char = vault_id + "-";
        let Role = _.trim(role_name.replace(replace_char, " "));

        let index = _.findIndex(this.custom_header.title, (t) => {
          return _.trim(t) == _.trim(Role);
        });

        if (index == -1) {
          return Role;
        } else {
          return role.title.replace(/.+?(\- )/g, "");
        }
      }
    },

    oneCharName(user) {
      if (user.first_name) {
        return user.first_name.substring(0, 1);
      }

      if (user.last_name) {
        return user.last_name.substring(0, 1);
      }

      return "-";
    },

    formatDate(date, format) {
      let d = moment(date);
      return d.format(format);
    },
  },

  computed: {
    ...mapGetters({
      roles: "getVaultRoles",
      role: "getUserRole",
      current_vault: "getCurrentVault",
    }),

    countSentence() {
      const total = this.members.total || 0;
      if (this.members.last_page > 1) {
        return `${this.members.from} to ${this.members.to} of ${total}`;
      }
      return total === 1 ? "1 member" : `${total} members`;
    },
  },
};
</script>

<style>
</style>
